import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Pencil } from "lucide-react";
import { MarkdownArticleBody } from "@/components/MarkdownArticleBody";
import { apiGet } from "@/lib/apiClient";
import { isBookCollectionKey } from "@/lib/knowledgeBaseRegistry";
import {
  articleEditPath,
  formatKnowledgeDate,
  knowledgeStatusLabel,
  type KnowledgeArticle,
} from "@/lib/knowledgeModel";
import {
  KnowledgeChrome,
  KnowledgeError,
  knowledgePrimaryLinkClass,
  knowledgeSecondaryLinkClass,
} from "@/pages/knowledge/KnowledgeChrome";
import { useCustomBooks } from "@/pages/knowledge/useCustomBooks";
import { knowledgeArticlesPath } from "@/lib/knowledgePaths";
import { useKnowledgeRouteBase } from "@/pages/knowledge/useKnowledgeRouteBase";

function bookLabels(article: KnowledgeArticle, titles: Map<string, string>): string {
  const keys = article.collectionKeys.filter(isBookCollectionKey);
  if (keys.length === 0) return "Standalone (no books)";
  return keys.map((key) => titles.get(key) || key).join(", ");
}

export function KnowledgeArticleViewPage() {
  const { articleId } = useParams();
  const baseId = useKnowledgeRouteBase();
  const { books: customBooks } = useCustomBooks();
  const titles = new Map(customBooks.map((book) => [book.collectionKey, book.title] as const));

  const [article, setArticle] = useState<KnowledgeArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!articleId) {
      setError("Article id is missing.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      const res = await apiGet<KnowledgeArticle & { error?: string }>(
        `/api/knowledge/articles/${encodeURIComponent(articleId)}`,
      );
      if (cancelled) return;
      if (!res.ok || !res.data?.id) {
        setError(res.error || "Could not load article.");
        setArticle(null);
        setLoading(false);
        return;
      }
      setArticle({
        ...res.data,
        markdown: typeof res.data.markdown === "string" ? res.data.markdown : "",
        collectionKeys: Array.isArray(res.data.collectionKeys) ? res.data.collectionKeys : [],
        status: res.data.status === "published" ? "published" : "draft",
      });
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [articleId]);

  return (
    <KnowledgeChrome
      title={article?.title || "Article"}
      description="Read this article here. Use Edit when you need to change the title, status, markdown, or books."
      badge={article ? knowledgeStatusLabel(article.status) : "Markdown"}
      actions={
        <>
          <Link to={knowledgeArticlesPath(baseId)} className={knowledgeSecondaryLinkClass}>
            Articles
          </Link>
          {article ? (
            <Link to={articleEditPath(baseId, article.id)} className={knowledgePrimaryLinkClass}>
              <Pencil className="h-4 w-4" aria-hidden />
              Edit
            </Link>
          ) : null}
        </>
      }
    >
      {error ? <KnowledgeError message={error} /> : null}
      {loading ? <p className="text-sm text-slate-500">Loading article…</p> : null}
      {!loading && article ? (
        <article className="max-w-3xl space-y-6 rounded-lg border border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-slate-950/40">
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="font-medium text-slate-500 dark:text-slate-400">Status</dt>
              <dd className="mt-0.5 text-slate-900 dark:text-white">
                {knowledgeStatusLabel(article.status)}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-slate-500 dark:text-slate-400">Updated</dt>
              <dd className="mt-0.5 text-slate-900 dark:text-white">
                {formatKnowledgeDate(article.updatedAt)}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="font-medium text-slate-500 dark:text-slate-400">Books</dt>
              <dd className="mt-0.5 text-slate-900 dark:text-white">{bookLabels(article, titles)}</dd>
            </div>
          </dl>
          <div>
            <h2 className="text-sm font-medium text-slate-500 dark:text-slate-400">Body</h2>
            <MarkdownArticleBody markdown={article.markdown} />
          </div>
        </article>
      ) : null}
    </KnowledgeChrome>
  );
}
